// src/pages/KoleksiBuku.jsx - Responsive Mobile Version with Animation
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import LayoutUser from '../components/layoutUser';
import { FaSearch, FaFilter, FaTimes } from 'react-icons/fa';
import BookCard from '../components/BookCard';

const KoleksiBuku = () => {
    const [allBooks, setAllBooks] = useState([]);
    const [categories, setCategories] = useState([]);
    const [filteredBooks, setFilteredBooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [error, setError] = useState(null);
    
    // State untuk pagination
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    
    // State untuk mobile UI
    const [showFilters, setShowFilters] = useState(false);
    const [showInfoPanel, setShowInfoPanel] = useState(false);

    // Computed values untuk pagination
    const totalEntries = filteredBooks.length;
    const startIndex = (currentPage - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    const currentBooks = filteredBooks.slice(startIndex, endIndex);
    const totalPages = Math.ceil(totalEntries / entriesPerPage);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setError(null);
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Token tidak ditemukan. Silakan login kembali.');
                setLoading(false);
                return;
            }

            const headers = { 'x-auth-token': token };

            const [bukuResponse, kategoriResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/buku`, { headers }),
                axios.get(`${process.env.REACT_APP_API_URL}/kategori`, { headers })
            ]);

            setAllBooks(bukuResponse.data);
            setFilteredBooks(bukuResponse.data);
            setCategories(kategoriResponse.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Gagal memuat data. Silakan coba lagi.');
        } finally {
            setLoading(false);
        }
    };

    // Updated filtering logic untuk many-to-many categories
    useEffect(() => {
        const result = allBooks.filter(book => {
            const matchesSearch = book.judul.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (book.pengarang && book.pengarang.toLowerCase().includes(searchQuery.toLowerCase()));
            
            let matchesCategory = selectedCategory === '';
            
            if (selectedCategory !== '') {
                if (book.kategori_list && Array.isArray(book.kategori_list)) {
                    matchesCategory = book.kategori_list.includes(parseInt(selectedCategory));
                }
                else if (book.id_kategori) {
                    matchesCategory = book.id_kategori == selectedCategory;
                }
                else if (book.nama_kategori) {
                    const selectedCategoryName = categories.find(cat => cat.id == selectedCategory)?.nama_kategori;
                    if (selectedCategoryName) {
                        matchesCategory = book.nama_kategori.includes(selectedCategoryName);
                    }
                }
            }
            
            return matchesSearch && matchesCategory;
        });
        setFilteredBooks(result);
    }, [searchQuery, selectedCategory, allBooks, categories]);

    // Reset halaman saat filter berubah
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, selectedCategory]);

    const handleEntriesPerPageChange = (e) => {
        setEntriesPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    // Render pagination yang responsive
    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const maxVisiblePages = window.innerWidth < 768 ? 3 : 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '4px',
                marginTop: '20px',
                flexWrap: 'wrap'
            }}>
                <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    style={{
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        backgroundColor: currentPage === 1 ? '#f5f5f5' : '#fff',
                        color: currentPage === 1 ? '#999' : '#333',
                        borderRadius: '4px',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        fontSize: '14px'
                    }}
                >
                    Prev
                </button>
                
                {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                    const pageNum = startPage + i;
                    return (
                        <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            style={{
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                backgroundColor: currentPage === pageNum ? '#3273dc' : '#fff',
                                color: currentPage === pageNum ? '#fff' : '#333',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                minWidth: '40px'
                            }}
                        >
                            {pageNum}
                        </button>
                    );
                })}
                
                <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    style={{
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        backgroundColor: currentPage === totalPages ? '#f5f5f5' : '#fff',
                        color: currentPage === totalPages ? '#999' : '#333',
                        borderRadius: '4px',
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        fontSize: '14px'
                    }}
                >
                    Next
                </button>
            </div>
        );
    };

    const containerStyles = {
        padding: window.innerWidth < 768 ? '10px' : '20px',
        display: 'flex',
        flexDirection: window.innerWidth < 1024 ? 'column' : 'row',
        gap: window.innerWidth < 768 ? '16px' : '24px'
    };

    const leftColumnStyles = {
        flex: window.innerWidth < 1024 ? '1' : '1',
        maxWidth: window.innerWidth < 1024 ? '100%' : '70%'
    };

    const rightColumnStyles = {
        flex: window.innerWidth < 1024 ? '1' : '0 0 30%',
        backgroundColor: '#fff',
        padding: window.innerWidth < 768 ? '15px' : '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        height: 'fit-content'
    };

    return (
        <LayoutUser title="Koleksi Buku">
            {/* --- PENAMBAHAN ANIMASI DI SINI --- */}
            <style>{`
                @keyframes slideInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .book-card-animated {
                    opacity: 0; /* Mulai dari transparan */
                    animation: slideInUp 0.5s ease-out forwards;
                }
            `}</style>
            
            <div style={containerStyles}>
                {/* Left Column */}
                <div style={leftColumnStyles}>
                    {/* Mobile Filter Toggle */}
                    <div style={{
                        display: window.innerWidth < 768 ? 'flex' : 'none',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '15px',
                        padding: '10px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px'
                    }}>
                        <span style={{ fontWeight: '500' }}>Filter & Pencarian</span>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            style={{
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                backgroundColor: showFilters ? '#3273dc' : '#fff',
                                color: showFilters ? '#fff' : '#333',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '14px'
                            }}
                        >
                            <FaFilter size={12} />
                            {showFilters ? 'Tutup' : 'Filter'}
                        </button>
                    </div>

                    {/* Search and Filter Section */}
                    <div style={{
                        marginBottom: '20px',
                        display: window.innerWidth < 768 ? (showFilters ? 'block' : 'none') : 'block'
                    }}>
                        {/* Show Entries */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '12px',
                            gap: '8px',
                            flexWrap: 'wrap'
                        }}>
                            <span style={{ fontSize: '14px', color: '#666' }}>Show</span>
                            <select
                                value={entriesPerPage}
                                onChange={handleEntriesPerPageChange}
                                style={{
                                    padding: '6px 8px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    width: '70px'
                                }}
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span style={{ fontSize: '14px', color: '#666' }}>entries</span>
                        </div>

                        {/* Search Input */}
                        <div style={{
                            position: 'relative',
                            marginBottom: '12px'
                        }}>
                            <input
                                type="text"
                                placeholder="Cari buku berdasarkan judul atau pengarang..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '12px 12px 12px 40px',
                                    border: '1px solid #ccc',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    boxSizing: 'border-box'
                                }}
                            />
                            <FaSearch
                                style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#aaa'
                                }}
                            />
                        </div>

                        {/* Category Filter */}
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            style={{
                                padding: '12px',
                                border: '1px solid #ccc',
                                borderRadius: '8px',
                                fontSize: '14px',
                                width: window.innerWidth < 768 ? '100%' : '200px',
                                boxSizing: 'border-box'
                            }}
                        >
                            <option value="">Semua Kategori</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.id}>{category.nama_kategori}</option>
                            ))}
                        </select>

                        {/* Clear Filters Button (Mobile) */}
                        {window.innerWidth < 768 && (searchQuery || selectedCategory) && (
                            <button
                                onClick={() => {
                                    setSearchQuery('');
                                    setSelectedCategory('');
                                }}
                                style={{
                                    marginTop: '10px',
                                    padding: '8px 16px',
                                    border: '1px solid #dc3545',
                                    backgroundColor: '#fff',
                                    color: '#dc3545',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <FaTimes size={12} />
                                Clear Filters
                            </button>
                        )}
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div style={{
                            padding: '16px',
                            backgroundColor: '#ffeaea',
                            color: '#c53030',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            fontSize: '14px'
                        }}>
                            {error}
                        </div>
                    )}

                    {/* Results Info */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '15px',
                        fontSize: '14px',
                        color: '#666',
                        flexWrap: 'wrap',
                        gap: '10px'
                    }}>
                        <span>
                            Showing {startIndex + 1} to {Math.min(endIndex, totalEntries)} of {totalEntries} entries
                        </span>
                        
                        {/* Info Panel Toggle for Mobile */}
                        {window.innerWidth < 1024 && (
                            <button
                                onClick={() => setShowInfoPanel(!showInfoPanel)}
                                style={{
                                    padding: '6px 12px',
                                    border: '1px solid #ddd',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                }}
                            >
                                {showInfoPanel ? 'Tutup Info' : 'Info'}
                            </button>
                        )}
                    </div>

                    {/* Book List */}
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                            <p>Memuat data buku...</p>
                        </div>
                    ) : (
                        <div>
                            {currentBooks.length > 0 ? (
                                <>
                                    <div style={{
                                        display: 'grid',
                                        gap: '15px'
                                    }}>
                                        {/* --- PERUBAHAN UNTUK MENERAPKAN ANIMASI --- */}
                                        {currentBooks.map((book, index) => (
                                            <div 
                                                key={`koleksi-buku-${book.id}`}
                                                className="book-card-animated"
                                                style={{ animationDelay: `${index * 0.05}s` }}
                                            >
                                                <BookCard book={book} />
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {/* Pagination */}
                                    {renderPagination()}
                                </>
                            ) : (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '40px',
                                    color: '#666'
                                }}>
                                    <p>Tidak ada buku yang ditemukan</p>
                                    {(searchQuery || selectedCategory) && (
                                        <button
                                            onClick={() => {
                                                setSearchQuery('');
                                                setSelectedCategory('');
                                            }}
                                            style={{
                                                marginTop: '10px',
                                                padding: '8px 16px',
                                                border: '1px solid #3273dc',
                                                backgroundColor: '#3273dc',
                                                color: '#fff',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                fontSize: '14px'
                                            }}
                                        >
                                            Reset Filter
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Right Column - Information Panel */}
                <div style={{
                    ...rightColumnStyles,
                    display: window.innerWidth < 1024 ? (showInfoPanel ? 'block' : 'none') : 'block'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '15px'
                    }}>
                        <h2 style={{
                            margin: '0',
                            borderBottom: '1px solid #ddd',
                            paddingBottom: '5px',
                            fontSize: window.innerWidth < 768 ? '20px' : '28px',
                            fontWeight: '600',
                            color: '#333',
                            flex: '1'
                        }}>
                            Informasi
                        </h2>
                        
                        {/* Close button for mobile */}
                        {window.innerWidth < 1024 && (
                            <button
                                onClick={() => setShowInfoPanel(false)}
                                style={{
                                    padding: '4px',
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    cursor: 'pointer',
                                    color: '#666'
                                }}
                            >
                                <FaTimes />
                            </button>
                        )}
                    </div>

                    <p style={{
                        marginBottom: '10px',
                        lineHeight: '1.6',
                        color: '#333',
                        fontSize: window.innerWidth < 768 ? '14px' : '16px'
                    }}>
                        Modul/Diktat memiliki hakcipta, tidak dibenarkan untuk mengunduh,
                        menyebarluaskan atau mempublikasikan ulang tanpa
                        <strong> izin tertulis </strong> dari
                        <strong> Perpustakaan SMPN 4 Tilatang Kamang.</strong>
                    </p>
                </div>
            </div>
        </LayoutUser>
    );
};

export default KoleksiBuku;